/**
 * Prepares a customer's stored email HTML for the workspace thread.
 *
 * Email HTML is attacker-influenced and was written for a mail client, not for a bubble inside our
 * dashboard. Three things go wrong if it is rendered as-is:
 *
 * - The quoted reply chain comes along, so every customer bubble repeats the thread. The helpdesks
 *   we read store no HTML without the quote (Kim Helpdesk's `html_content.reply` is Html2Text
 *   output and Gorgias puts `body_text` there, so neither is HTML), which is why the tail is cut
 *   here from `full`.
 * - Its CSS stays: the email renders in a sandboxed frame (`EmailHtmlContent`) that contains it.
 * - Its images show, as in any helpdesk, but tracking pixels are dropped so the sender never sees the open.
 *
 * The HTML never goes through `marked`: markdown treats indented lines as a code block, which turned
 * most Gmail and Outlook bodies into escaped source.
 */

import DOMPurify from "dompurify";

/** Markers mail clients put on the quoted history. */
const QUOTED_HISTORY_SELECTORS = [
  ".gmail_quote",
  'blockquote[type="cite"]',
  // Gorgias and Yahoo classes reach us prefixed when the mail was forwarded through Yahoo or Outlook (yiv123gorgias_quote).
  '[class*="gorgias_extra"]',
  '[class*="gorgias_quote"]',
  '[class*="yahoo_quoted"]',
  // Yahoo iOS's "On …, wrote:" line and quote; Yahoo and AOL Android's.
  ".yahoo-quoted-begin",
  "blockquote.iosymail",
  ".aol-quote-attribution",
  ".aol-quote-body",
  ".protonmail_quote",
  ".zendesk_quote",
];

/** Outlook's "From: / Sent:" header (web, then desktop's ruled one); it and everything after it is the quoted mail. */
const OUTLOOK_REPLY_HEADER_SELECTORS = ["#divRplyFwdMsg", 'div[style*="border-top:solid #E1E1E1"]', 'div[style*="border-top: solid #E1E1E1"]'];

/** Samsung and other Android mail mark the quote only with this line. */
const ORIGINAL_MESSAGE_LINE = /^-{4,}\s*Original message\s*-{4,}/i;

/** The "On …, X wrote:" line a quote starts with; Yahoo web and some mobile apps give it no class. */
const WROTE_LINE = /^On\s[\s\S]{5,250}\swrote:$/;

/** Set on every node of the quoted history; the frame hides them until the "…" toggle. */
const QUOTED_ATTRIBUTE = "data-quoted";
/** On the frame's root while the quoted history is hidden. */
export const QUOTE_HIDDEN_CLASS = "quote-hidden";

/** Markup that reaches outside the bubble (global CSS, scripts, embeds, page metadata) or posts data. */
// Media tags are dropped too: like a remote <img>, a <video poster>, <audio> or <source> loads from the
// sender's server and tells them the operator opened the email.
const FORBIDDEN_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "form",
  "link",
  "meta",
  "video",
  "audio",
  "source",
  "track",
  "picture",
];

/** `background` loads a remote image like `<img>` does. Inline styles stay: the frame contains them. */
const FORBIDDEN_ATTRIBUTES = ["background"];

/** Open-tracking endpoints of the common senders (Mailchimp, SendGrid, Mandrill, HubSpot, Klaviyo…). */
const TRACKING_URL = /\/(track\/)?open(\.php|\.gif|\.aspx)?(\?|\/|$)|\/wf\/open|\/o\.gif|\/(pixel|beacon)(\.gif|\.png)?(\?|\/|$)|track\.hubspot|trk\.klaviyo/i;

/** A size of 0 or 1px, from the attribute or the inline style. */
function isTinySize(image: Element, side: "width" | "height", style: string): boolean {
  const attribute = Number(image.getAttribute(side));
  if (image.hasAttribute(side) && Number.isFinite(attribute) && attribute <= 1) return true;
  return new RegExp(`(^|[;\\s])${side}\\s*:\\s*[01](px)?\\s*(;|$|!)`, "i").test(style);
}

/** An image that exists only to report an open: invisible, 1x1, or sent to an open-tracking URL. */
function isTrackingPixel(image: Element): boolean {
  const style = image.getAttribute("style") ?? "";
  if (/display\s*:\s*none|visibility\s*:\s*hidden|opacity\s*:\s*0(\.0+)?\s*(;|$|!)/i.test(style)) return true;
  if (isTinySize(image, "width", style) || isTinySize(image, "height", style)) return true;
  return TRACKING_URL.test(image.getAttribute("src") ?? "");
}

/** Whether a parsed body still shows the reader anything. */
function hasVisibleContent(body: HTMLElement): boolean {
  return (
    (body.textContent ?? "").trim() !== "" || body.querySelector("img") !== null
  );
}

/** Marks a node as quoted history; a bare text node is wrapped, since only an element can carry the attribute. */
function markQuoted(node: ChildNode): void {
  if (node instanceof Element) {
    node.setAttribute(QUOTED_ATTRIBUTE, "");
  } else if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim() !== "") {
    const wrapper = node.ownerDocument!.createElement("span");
    wrapper.setAttribute(QUOTED_ATTRIBUTE, "");
    node.replaceWith(wrapper);
    wrapper.append(node);
  }
}

/** Marks `start` and everything after it in the email, at every level up to the body. */
function markFrom(start: ChildNode, body: HTMLElement): void {
  // A "wrote:" line inside an already marked quote is part of it.
  if (start.parentElement?.closest(`[${QUOTED_ATTRIBUTE}]`)) return;
  // Outlook draws a rule just above the header; it belongs to the quote, not to the reply.
  if (start instanceof Element && start.previousElementSibling?.tagName === "HR") markQuoted(start.previousElementSibling);
  for (let node: Node | null = start; node && node !== body; node = node.parentNode) {
    // Collected first: wrapping a text node moves it into a span, which would end a live nextSibling walk.
    const siblings: ChildNode[] = [];
    for (let sibling = node.nextSibling; sibling; sibling = sibling.nextSibling) siblings.push(sibling);
    siblings.forEach(markQuoted);
  }
  markQuoted(start);
}

/** Marks a quote flagged only by a reply header (Outlook) or an "Original message" line (Android); whether there was one. */
function markReplyHeaderTail(body: HTMLElement): boolean {
  const outlook = Array.from(body.querySelectorAll(OUTLOOK_REPLY_HEADER_SELECTORS.join(","))).find((header) =>
    /From:/.test(header.textContent ?? ""),
  );
  const android = Array.from(body.querySelectorAll("div")).find(
    (div) => !div.querySelector("div") && ORIGINAL_MESSAGE_LINE.test((div.textContent ?? "").trim()),
  );
  const start = outlook ?? android;
  if (!start) return false;
  markFrom(start, body);
  return true;
}

/** Marks from the first "On …, X wrote:" line, its own text or its whole parent block; whether there was one. */
function markWroteTail(body: HTMLElement): boolean {
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!/^\s*On\s/.test(node.textContent ?? "")) continue;
    // The sender's mailto link splits the line, so its parent block is checked as well as the text itself.
    const parent = node.parentElement;
    const start = WROTE_LINE.test((node.textContent ?? "").trim())
      ? (node as ChildNode)
      : parent && parent !== body && WROTE_LINE.test((parent.textContent ?? "").trim())
        ? parent
        : null;
    if (start) {
      markFrom(start, body);
      return true;
    }
  }
  return false;
}

/** Marks the blank lines (bare <br>s, empty blocks) between the reply and its quote, so the folded email ends at its last line. */
function markBlankLinesBeforeQuote(body: HTMLElement): void {
  const isBlank = (node: ChildNode): boolean =>
    (node.textContent ?? "").trim() === "" && !(node instanceof Element && (node.matches("img") || node.querySelector("img")));
  // Up from the quote to the first level with reply content before it...
  let reply: ChildNode | null = null;
  for (let node: Node | null = body.querySelector(`[${QUOTED_ATTRIBUTE}]`); node && node !== body && !reply; node = node.parentNode) {
    for (reply = node.previousSibling; reply && isBlank(reply); reply = reply.previousSibling) markQuoted(reply);
  }
  // ...then down the reply's last block, where Gmail puts the blank lines; a table is left whole, its cells keep their grid.
  while (reply instanceof Element && reply.tagName !== "TABLE") {
    let last = reply.lastChild;
    for (; last && isBlank(last); last = last.previousSibling) markQuoted(last);
    reply = last;
  }
}

/** The parsed email with its quoted reply chain marked, and whether a quote with visible content before it was found. */
function markedBody(html: string): { body: HTMLElement; styles: string; hasQuote: boolean } {
  const body = new DOMParser().parseFromString(html, "text/html").body;
  // The email's CSS sits in its <head>; carried along so the frame can lay the email out.
  const styles = Array.from(body.ownerDocument.head.querySelectorAll("style"), (style) => style.outerHTML).join("");
  const quotes = body.querySelectorAll(QUOTED_HISTORY_SELECTORS.join(","));
  quotes.forEach(markQuoted);
  const markedHeader = markReplyHeaderTail(body);
  const markedWrote = markWroteTail(body);
  if (quotes.length === 0 && !markedHeader && !markedWrote) return { body, styles, hasQuote: false };
  // A bare forward is all quote: with nothing of the customer's own left, the whole email is the message.
  const rest = body.cloneNode(true) as HTMLElement;
  rest.querySelectorAll(`[${QUOTED_ATTRIBUTE}]`).forEach((node) => node.remove());
  if (!hasVisibleContent(rest)) {
    body.querySelectorAll(`[${QUOTED_ATTRIBUTE}]`).forEach((node) => node.removeAttribute(QUOTED_ATTRIBUTE));
    return { body, styles, hasQuote: false };
  }
  return { body, styles, hasQuote: true };
}

/**
 * The customer's email with its quoted reply chain marked `data-quoted`, so the frame can hide it
 * behind the bubble's "…" toggle without a second copy of the email; `hasQuote` says whether
 * there is anything to toggle.
 */
export function markQuotedTail(html: string): { html: string; hasQuote: boolean } {
  const { body, styles, hasQuote } = markedBody(html);
  if (hasQuote) markBlankLinesBeforeQuote(body);
  return { html: styles + body.innerHTML, hasQuote };
}

/** An agent's reply without the customer mail it quoted: the bubble above already shows it. */
export function withoutQuotedTail(html: string): string {
  const { body, styles } = markedBody(html);
  body.querySelectorAll(`[${QUOTED_ATTRIBUTE}]`).forEach((node) => node.remove());
  return styles + body.innerHTML;
}

/** Opens every link in a new tab without giving the target page a handle on the dashboard. */
function hardenLinks(root: DocumentFragment): void {
  root.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
}

/** Drops tracking pixels and images with no source; every other image stays and loads in the frame. */
function removeTrackingPixels(root: DocumentFragment): void {
  root.querySelectorAll("img").forEach((image) => {
    if (!image.getAttribute("src") || isTrackingPixel(image)) image.remove();
  });
}

/**
 * The address to open for a click in the email frame: an image not already in a link, so a pasted-in
 * screenshot opens full size as an attached image does. Read from the resolved `src`, which turns
 * `HTTPS://` and `//host/` into plain `https://`; null for anything else, including a `data:` image.
 * Opened on click rather than wrapped in a link, so the email's own `td > img` styles still apply.
 */
export function clickedImageUrl(target: EventTarget | null): string | null {
  // nodeName, not instanceof: the click comes from the frame's document, whose element classes are its own.
  const image = target as HTMLImageElement | null;
  if (image?.nodeName !== "IMG" || image.closest("a")) return null;
  return image.src.startsWith("https://") ? image.src : null;
}

/** Sanitised HTML safe to render in the email frame. */
export function sanitizeEmailHtml(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
    RETURN_DOM_FRAGMENT: true,
  });
  hardenLinks(fragment);
  removeTrackingPixels(fragment);
  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML;
}

/** The frame's page: https images only, nothing else remote; the light canvas emails are designed for, in our font. */
export function emailFrameDocument(safeHtml: string, fontFamily: string): string {
  return [
    `<!doctype html><html class="${QUOTE_HIDDEN_CLASS}"><head>`,
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: data:; font-src data:">`,
    '<base target="_blank">',
    // The root's height is the email's own: the frame is sized from it, so an email's html{height:100%} must not apply.
    `<style>html{color-scheme:light;height:auto!important}body{margin:0;background:#ffffff;color:#2b2e34;font-family:${fontFamily.replace(/[<>]/g, "")},ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;font-size:13px;line-height:1.5;overflow-wrap:anywhere;overflow-x:auto}img{max-width:100%;height:auto}img:not(a img){cursor:zoom-in}body *{font-family:inherit!important}.${QUOTE_HIDDEN_CLASS} [${QUOTED_ATTRIBUTE}]{display:none!important}</style>`,
    "</head><body>",
    safeHtml,
    "</body></html>",
  ].join("");
}

/** The structure an agent's reply carries; its colours, fonts and layout are left behind. */
const FORMATTING_TAGS = [
  "p", "br", "div", "span", "ul", "ol", "li", "strong", "b", "em", "i", "u", "s", "a", "blockquote",
  "h1", "h2", "h3", "h4", "h5", "h6", "pre", "code", "hr", "img", "table", "thead", "tbody", "tr", "th", "td",
];
/** Width and height stay so an emoji or logo keeps its size; the prose styles cap both at the bubble. */
const FORMATTING_ATTRIBUTES = ["href", "src", "alt", "title", "target", "rel", "width", "height"];

/**
 * An agent's reply as formatting only, to render inline with our own typography.
 *
 * Helpdesk editors write structure (paragraphs, lists, bold, links), not designed emails, so no
 * frame is needed. The full sanitiser runs first, because tracking pixels are spotted by the
 * size and style attributes this pass then removes.
 */
export function formattingOnlyHtml(html: string): string {
  return DOMPurify.sanitize(sanitizeEmailHtml(html), {
    ALLOWED_TAGS: FORMATTING_TAGS,
    ALLOWED_ATTR: FORMATTING_ATTRIBUTES,
    ALLOW_DATA_ATTR: false,
  });
}
